// WebSocket configuration
// Cloud Run backend URL
const CLOUD_RUN_URL = "wss://catalyst-engine-477340841782.us-central1.run.app";
const LOCAL_URL = "ws://localhost:8080";

// Set to true to use Cloud Run, false for local development
const USE_CLOUD_RUN = true;

export const WS_URL = USE_CLOUD_RUN ? CLOUD_RUN_URL : LOCAL_URL;

// Status colors
export const STATUS_COLORS = {
  OK: "#10b981",      // green
  WARNING: "#f59e0b", // amber
  ALERT: "#ef4444",   // red
  UNKNOWN: "#6b7280", // gray
  INIT: "#6366f1",    // indigo (initializing)
  WARMING: "#8b5cf6", // purple (warming up)
};

// Risk score thresholds
export const RISK_THRESHOLDS = {
  LOW: 0.3,
  MEDIUM: 0.5,
  HIGH: 0.7,
};

// Complete sensor information (NASA C-MAPSS turbofan)
// 21 sensors total, but some are constant in FD001 dataset
export const SENSOR_INFO = {
  sensor_1: {
    name: "Fan Inlet Temp",
    shortName: "T1",
    unit: "°R",
    description: "Total temperature at fan inlet",
    significance: "Ambient conditions indicator",
    importance: "low", // constant in FD001
  },
  sensor_2: {
    name: "LPC Outlet Temp",
    shortName: "T2",
    unit: "°R",
    description: "Total temperature at Low Pressure Compressor outlet",
    significance: "Rising trend indicates compressor degradation",
    importance: "high",
  },
  sensor_3: {
    name: "HPC Outlet Temp",
    shortName: "T24",
    unit: "°R",
    description: "Total temperature at High Pressure Compressor outlet",
    significance: "Key indicator of compression efficiency loss",
    importance: "high",
  },
  sensor_4: {
    name: "LPT Outlet Temp",
    shortName: "T50",
    unit: "°R",
    description: "Total temperature at Low Pressure Turbine outlet",
    significance: "Indicates turbine section health",
    importance: "high",
  },
  sensor_5: {
    name: "Fan Inlet Pressure",
    shortName: "P1",
    unit: "psia",
    description: "Pressure at fan inlet",
    significance: "Ambient pressure indicator",
    importance: "low", // constant in FD001
  },
  sensor_6: {
    name: "Bypass Duct Pressure",
    shortName: "P15",
    unit: "psia",
    description: "Total pressure in bypass duct",
    significance: "Fan performance indicator",
    importance: "low", // constant in FD001
  },
  sensor_7: {
    name: "HPC Outlet Pressure",
    shortName: "P30",
    unit: "psia",
    description: "Total pressure at High Pressure Compressor outlet",
    significance: "CRITICAL - Dropping pressure = compressor degradation",
    importance: "critical",
  },
  sensor_8: {
    name: "Physical Fan Speed",
    shortName: "Nf",
    unit: "rpm",
    description: "Physical rotational speed of the fan",
    significance: "Speed changes indicate load/efficiency issues",
    importance: "medium",
  },
  sensor_9: {
    name: "Physical Core Speed",
    shortName: "Nc",
    unit: "rpm",
    description: "Physical rotational speed of the core (HP spool)",
    significance: "Core speed variations indicate engine stress",
    importance: "medium",
  },
  sensor_10: {
    name: "Engine Pressure Ratio",
    shortName: "EPR",
    unit: "",
    description: "Ratio of turbine outlet to compressor inlet pressure",
    significance: "Overall engine thrust indicator",
    importance: "low", // constant in FD001
  },
  sensor_11: {
    name: "HPC Static Pressure",
    shortName: "Ps30",
    unit: "psia",
    description: "Static pressure at High Pressure Compressor outlet",
    significance: "CRITICAL - Key degradation indicator, used in fuel ratio",
    importance: "critical",
  },
  sensor_12: {
    name: "Fuel Flow Ratio",
    shortName: "phi",
    unit: "pps/psi",
    description: "Ratio of fuel flow to HPC static pressure (Ps30)",
    significance: "CRITICAL - Increasing ratio = efficiency loss",
    importance: "critical",
  },
  sensor_13: {
    name: "Corrected Fan Speed",
    shortName: "NRf",
    unit: "rpm",
    description: "Fan speed corrected for inlet conditions",
    significance: "Normalized fan performance metric",
    importance: "medium",
  },
  sensor_14: {
    name: "Corrected Core Speed",
    shortName: "NRc",
    unit: "rpm",
    description: "Core speed corrected for inlet conditions",
    significance: "Normalized core performance metric",
    importance: "medium",
  },
  sensor_15: {
    name: "Bypass Ratio",
    shortName: "BPR",
    unit: "",
    description: "Ratio of bypass air to core air flow",
    significance: "Fan/core balance indicator",
    importance: "medium",
  },
  sensor_16: {
    name: "Burner Fuel-Air Ratio",
    shortName: "farB",
    unit: "",
    description: "Fuel-to-air ratio in combustion chamber",
    significance: "Combustion efficiency indicator",
    importance: "low", // constant in FD001
  },
  sensor_17: {
    name: "Bleed Enthalpy",
    shortName: "htBleed",
    unit: "BTU/s",
    description: "Enthalpy of bleed air from compressor",
    significance: "Thermal energy extraction indicator",
    importance: "medium",
  },
  sensor_18: {
    name: "Demanded Fan Speed",
    shortName: "Nf_dmd",
    unit: "rpm",
    description: "Demanded (commanded) fan speed",
    significance: "Control system target",
    importance: "low", // constant in FD001
  },
  sensor_19: {
    name: "Demanded Corr Fan Speed",
    shortName: "PCNfR_dmd",
    unit: "rpm",
    description: "Demanded corrected fan speed",
    significance: "Control system target (corrected)",
    importance: "low", // constant in FD001
  },
  sensor_20: {
    name: "HPT Coolant Bleed",
    shortName: "W31",
    unit: "lbm/s",
    description: "High Pressure Turbine coolant bleed flow",
    significance: "Turbine cooling system health",
    importance: "medium",
  },
  sensor_21: {
    name: "LPT Coolant Bleed",
    shortName: "W32",
    unit: "lbm/s",
    description: "Low Pressure Turbine coolant bleed flow",
    significance: "Turbine cooling system health",
    importance: "medium",
  },
};

// Sensors that vary in FD001 (useful for analysis)
export const USEFUL_SENSORS = [
  "sensor_2", "sensor_3", "sensor_4", "sensor_7", "sensor_8", "sensor_9",
  "sensor_11", "sensor_12", "sensor_13", "sensor_14", "sensor_15", "sensor_17",
  "sensor_20", "sensor_21"
];

// Critical sensors for degradation detection
export const CRITICAL_SENSORS = ["sensor_7", "sensor_11", "sensor_12"];

// Primary display sensors (shown by default)
export const PRIMARY_SENSORS = ["sensor_2", "sensor_7", "sensor_11", "sensor_12", "sensor_4", "sensor_3"];

// Legacy exports for backwards compatibility
export const SENSOR_NAMES = Object.fromEntries(
  Object.entries(SENSOR_INFO).map(([key, val]) => [key, val.name])
);

export const SENSOR_UNITS = Object.fromEntries(
  Object.entries(SENSOR_INFO).map(([key, val]) => [key, val.unit])
);

// Metric explanations
export const METRIC_INFO = {
  mean: {
    name: "Mean",
    description: "Average value over the window period",
    interpretation: "Baseline level - compare to normal operating range",
  },
  std: {
    name: "Std Dev",
    description: "Standard deviation - measures variability/noise",
    interpretation: "Higher values = more unstable readings, potential sensor or component issues",
  },
  slope: {
    name: "Slope",
    description: "Rate of change per cycle (linear trend)",
    interpretation: "Negative slope often indicates degradation; positive may indicate compensation",
  },
};

// Risk level explanations
export const RISK_INFO = {
  LOW: {
    range: "0-30%",
    meaning: "Normal operation",
    action: "Continue monitoring",
    color: STATUS_COLORS.OK,
  },
  MODERATE: {
    range: "30-50%",
    meaning: "Early signs of degradation",
    action: "Increase monitoring frequency",
    color: "#eab308", // yellow
  },
  ELEVATED: {
    range: "50-70%",
    meaning: "Significant degradation detected",
    action: "Schedule maintenance inspection",
    color: STATUS_COLORS.WARNING,
  },
  CRITICAL: {
    range: "70-100%",
    meaning: "High failure probability",
    action: "Immediate maintenance required",
    color: STATUS_COLORS.ALERT,
  },
};

// Chart colors
export const CHART_COLORS = {
  primary: "#3b82f6",   // blue
  secondary: "#8b5cf6", // purple
  tertiary: "#06b6d4",  // cyan
  quaternary: "#f97316", // orange
};

// Maximum data points to keep in charts
export const MAX_CHART_POINTS = 100;

// Tooltip explanations for UI elements
export const TOOLTIPS = {
  // Fleet Dashboard tooltips
  fleetHealth: "% of engines below 30% risk. Green = healthy.",
  riskDistribution: "Engine count per risk level. High-end clusters = fleet-wide issue.",
  fleetRiskTrend: "Avg & max risk over time. Rising = spreading degradation.",

  // Engine-specific tooltips
  confidence: "Model certainty. Below 70% = limited data, improves over time.",
  topIndicators: "Sensors driving this risk score.",

  // Feature panel tooltips
  slopeMetric: "Change per cycle. Negative = degrading.",
  stdMetric: "Reading variability. High = unstable.",
  meanMetric: "Average value in window.",

  // Risk gauge tooltips
  riskScore: "Failure probability from sensor patterns.",
  mlPrediction: "ML estimate of remaining cycles until maintenance.",
};
